import { useMemo } from "react"
import { Configure, Hits, Index, InstantSearch, SearchBox, Stats, useInstantSearch } from "react-instantsearch"
import { Modal, ModalHeader, ModalBody } from "reactstrap"

import { CarHit, ConversationHit, DriverHit, TaskHit } from "./SearchHits"
import './GlobalSearch.css'

const HIGHLIGHT_PRE_TAG = "__ais-highlight__"
const HIGHLIGHT_POST_TAG = "__/ais-highlight__"

const stripPrefix = (indexName) => {
  return indexName.replace(/^(local_|staging_|production_)/, "")
}

const INDEX_CONFIG = {
  drivers: {
    title: "Drivers",
    hitComponent: DriverHit,
    hitsPerPage: 5,
  },
  cars: {
    title: "Cars",
    hitComponent: CarHit,
    hitsPerPage: 5,
  },
  conversations: {
    title: "Conversations",
    hitComponent: ConversationHit,
    hitsPerPage: 5,
  },
  tasks: {
    title: "Tasks",
    hitComponent: TaskHit,
    hitsPerPage: 5,
  },
}

function HideOnEmpty({ children }) {
  const { results } = useInstantSearch()

  if (!results || results.nbHits === 0) {
    return null
  }

  return <>{children}</>
}

function IndexSection({ indexName }) {
  const config = INDEX_CONFIG[stripPrefix(indexName)]
  if (!config) {
    return null
  }

  const { title, hitComponent, hitsPerPage } = config

  return (
    <Index indexName={indexName}>
      <Configure hitsPerPage={hitsPerPage} attributesToHighlight={["*"]} />

      <HideOnEmpty>
        <section className="search-section">
          <header className="search-section-header">
            <h3>{title}</h3>
            <Stats
              translations={{
                rootElementText: ({ nbHits }) =>
                  `${nbHits} ${nbHits === 1 ? "result" : "results"}`,
              }}
            />
          </header>

          <Hits hitComponent={hitComponent} />
        </section>
      </HideOnEmpty>
    </Index>
  )
}

function normalizeSearchUrl(searchUrl) {
  return searchUrl.replace(/\/+$/, "")
}

function toArray(value, fallback = []) {
  if (Array.isArray(value)) return value
  if (typeof value === "string") return [value]
  return fallback
}

function stringifyHighlightValue(value) {
  if (typeof value === "string") return value
  if (value === undefined) return JSON.stringify(null)
  return JSON.stringify(value)
}

function wrapHighlightResult(value) {
  if (Array.isArray(value)) {
    return value.map((item) => wrapHighlightResult(item))
  }

  if (value && typeof value === "object") {
    return Object.keys(value).reduce((result, key) => {
      result[key] = wrapHighlightResult(value[key])
      return result
    }, {})
  }

  return {
    value: stringifyHighlightValue(value),
    matchLevel: "partial",
    matchedWords: [],
  }
}

function adaptHit(hit, page, hitsPerPage, hitIndex) {
  const { _formatted: formattedHit, ...documentFields } = hit
  const objectID = documentFields.objectID ?? documentFields.id ?? `${page}-${hitIndex}`

  return {
    ...documentFields,
    objectID: String(objectID),
    __position: page * hitsPerPage + hitIndex + 1,
    ...(formattedHit
      ? {
        _highlightResult: wrapHighlightResult(formattedHit),
        _snippetResult: wrapHighlightResult(formattedHit),
      }
      : {}),
  }
}

function adaptSearchResult(result, request) {
  const params = request.params || {}
  const page = Number(params.page || 0)
  const hitsPerPage = Number(params.hitsPerPage || result.hitsPerPage || 20)
  const nbHits = Number(result.totalHits ?? result.estimatedTotalHits ?? 0)
  const nbPages = Number(
    result.totalPages ?? Math.ceil(nbHits / Math.max(hitsPerPage, 1))
  )

  return {
    index: request.indexName,
    hits: (result.hits || []).map((hit, hitIndex) =>
      adaptHit(hit, page, hitsPerPage, hitIndex)
    ),
    nbHits,
    page,
    nbPages,
    hitsPerPage,
    processingTimeMS: result.processingTimeMs || 0,
    query: result.query || params.query || "",
    params: "",
    exhaustiveNbHits: false,
    facets: result.facetDistribution || {},
    facets_stats: result.facetStats || {},
  }
}

function createSearchClient(searchUrl, searchToken) {
  const cache = new Map()
  const endpoint = `${normalizeSearchUrl(searchUrl)}/multi-search`

  return {
    clearCache() {
      cache.clear()
    },
    addAlgoliaAgent() { },
    async search(requests) {
      const cacheKey = JSON.stringify(requests)
      const cachedResult = cache.get(cacheKey)
      if (cachedResult) {
        return cachedResult
      }

      const queries = requests.map(({ indexName, params = {} }) => {
        const page = Number(params.page || 0)
        const hitsPerPage = Number(params.hitsPerPage || 20)

        return {
          indexUid: indexName,
          q: params.query || "",
          page: page + 1,
          hitsPerPage,
          attributesToHighlight: toArray(params.attributesToHighlight, ["*"]),
          highlightPreTag: HIGHLIGHT_PRE_TAG,
          highlightPostTag: HIGHLIGHT_POST_TAG,
          ...(params.attributesToRetrieve
            ? { attributesToRetrieve: toArray(params.attributesToRetrieve) }
            : {}),
          ...(params.facets ? { facets: toArray(params.facets) } : {}),
        }
      })


      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${searchToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ queries }),
      })

      if (!response.ok) {
        const message = await response.text()
        throw new Error(message || `Search request failed with ${response.status}`)
      }

      const payload = await response.json()
      const result = {
        results: (payload.results || []).map((searchResult, index) =>
          adaptSearchResult(searchResult, requests[index])
        ),
      }

      cache.set(cacheKey, result)
      return result
    },
    async searchForFacetValues() {
      return []
    },
  }
}

export function GlobalSearch({ searchToken, searchUrl, indexes = [], initialQuery = "" }) {
  const enabledIndexes = useMemo(
    () =>
      indexes.filter((indexName) =>
        INDEX_CONFIG[stripPrefix(indexName)]
      ),
    [indexes]
  )

  const searchClient = useMemo(() => {
    if (!searchUrl || !searchToken) return null
    return createSearchClient(searchUrl, searchToken)
  }, [searchUrl, searchToken])

  const initialUiState = useMemo(() => {
    if (!initialQuery || !enabledIndexes.length) return undefined

    return {
      [enabledIndexes[0]]: {
        query: initialQuery,
      },
    }
  }, [enabledIndexes, initialQuery])

  if (!searchToken || !searchUrl || !indexes.length) {
    return <div>Global search not loaded</div>
  }

  if (!enabledIndexes.length || !searchClient) {
    return null
  }

  return (
    <InstantSearch searchClient={searchClient} indexName={enabledIndexes[0]} initialUiState={initialUiState} future={{ preserveSharedStateOnUnmount: true }}>
      <div className="global-search">
        <SearchBox placeholder="Search drivers, cars, conversations, tasks…" autoFocus searchAsYouType />

        <div className="search-results">
          {enabledIndexes.map((indexName) => (
            <IndexSection key={indexName} indexName={indexName} />
          ))}
        </div>
      </div>
    </InstantSearch>
  )
}

export function GlobalSearchModal({ isOpen, toggle, searchToken, searchUrl, indexes = [], initialQuery = "" }) {
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="global-search-modal" contentClassName="global-search-modal-content" backdropClassName="global-search-modal-backdrop">
      <ModalHeader className="global-search-modal-header">
        Search
      </ModalHeader>

      <ModalBody className="global-search-modal-body">
        <GlobalSearch searchToken={searchToken} searchUrl={searchUrl} indexes={indexes} initialQuery={initialQuery} />
      </ModalBody>
    </Modal>
  )
}